/**
 * rate-card — Resource Plan §4.3 / RP-12.2: the derived figures of a Rate Card.
 *
 *   Blended Rate per basis = Σ (mix % × that basis's bill rate)
 *   Blended Cost           = Σ (mix % × Avg Internal Cost)     — shared across all bases
 *   Card Margin % per basis = (Blended Rate − Blended Cost) ÷ Blended Rate
 *
 * All three are DERIVED AND READ-ONLY (RP-12.2). Target Margin % is never entered on a
 * Rate Card row — a hand-entered target drifts from the rates it claims to describe the
 * moment either changes.
 *
 * PURE MODULE — no `@/db`, no `Bun`, no server-only imports. The Configuration UI and the
 * API both import this file, so a card cannot render one number and save another. Same
 * discipline that splits permissions.ts (client-safe data) from permissions-server.ts.
 *
 * ──────────────────────────────────────────────────────────────────────────────
 * WHY THIS IS INTEGER ARITHMETIC, AND WHY NOTHING IS ROUNDED IN THE MIDDLE
 * ──────────────────────────────────────────────────────────────────────────────
 * The 2026 Standard Rate Card (RP-12.1) is the acceptance test for slice M1: the system
 * must reproduce $234.00 / $203.00 / $183.00 and 64.84% / 59.48% / 55.05% EXACTLY.
 *
 * Blended Cost is $82.2645, displayed as $82.26. Rounding it to $82.26 BEFORE the margin
 * division gives Fixed a Card Margin of 64.85%, not the published 64.84%:
 *
 *   exact:      (234.0000 − 82.2645) ÷ 234.0000 = 0.648442… → 64.84%   ✓
 *   pre-rounded:(234.0000 − 82.2600) ÷ 234.0000 = 0.648462… → 64.85%   ✗
 *
 * Enterprise and Standard are unaffected, so the mistake looks like a rounding quibble on
 * one cell rather than a bug. It is a bug. Rounding happens exactly once, at the
 * presentation boundary, in the formatters at the bottom of this file.
 *
 * Units, all exact integers:
 *   cents  — money at scale 2.            $106.03  → 10603
 *   bp     — percent at scale 2.           20.00%  → 2000   (10000 = 100.00%)
 *   micros — cents × bp = 1e-6 dollars. The blended numerators live here so that
 *            Σ(rate × mix) stays exact: $82.2645 is 82_264_500 micros, not a float.
 *
 * Magnitudes are safe: the largest intermediate is (blendedRateMicros × 10000 × 2) ≈
 * 4.7e12 for a $234 blended rate, three orders of magnitude below Number.MAX_SAFE_INTEGER
 * (9.007e15). assertSafe() guards the boundary rather than trusting that forever.
 */

/** Money at scale 2 — dollars × 100. */
export type Cents = number;
/** Percent at scale 2 — percent × 100. 10000 = 100.00%. */
export type Bp = number;
/** cents × bp = 1e-6 dollars. The unit blended numerators are computed in. */
export type Micros = number;

/** 100.00% expressed in bp — the mix invariant target and the margin denominator scale. */
export const FULL_PERCENT_BP: Bp = 10_000;

/** Micros per dollar (1e6): cents (1e-2) × bp-as-fraction (1e-4). */
const MICROS_PER_DOLLAR = 1_000_000;

// ── Boundary parsing ─────────────────────────────────────────────────────────

/**
 * Parse a Postgres `numeric` string into an exact integer at the given scale.
 *
 * Drizzle round-trips `numeric` as a **string** (seeds pass `"0.50"`), which is what
 * makes exactness possible — routing money through `Number()` first would reintroduce
 * the float error this module exists to avoid. Never call `Number()` on a rate, a cost,
 * or a mix percentage.
 *
 * Rejects more fraction digits than the scale allows rather than truncating: a silently
 * dropped digit on a bill rate is a pricing error, not a formatting one.
 */
export function parseMinor(value: string, scale: number): number {
  const match = /^(-?)(\d+)(?:\.(\d*))?$/.exec(value.trim());
  if (!match) {
    throw new RangeError(`not a decimal number: ${JSON.stringify(value)}`);
  }
  const sign = match[1] === "-" ? -1 : 1;
  const whole = match[2] ?? "0";
  const fraction = match[3] ?? "";
  if (fraction.length > scale) {
    throw new RangeError(
      `${JSON.stringify(value)} has ${fraction.length} decimal places; scale ${scale} allows ${scale}`,
    );
  }
  const padded = fraction.padEnd(scale, "0");
  const magnitude = Number(`${whole}${padded}`);
  assertSafe(magnitude, `parseMinor(${JSON.stringify(value)}, ${scale})`);
  return sign * magnitude;
}

function assertSafe(n: number, what: string): void {
  if (!Number.isSafeInteger(n)) {
    throw new RangeError(`${what} exceeded exact integer range (got ${n})`);
  }
}

// ── Inputs ───────────────────────────────────────────────────────────────────

/** A Labor Category as the card needs it. Strings are raw Postgres `numeric` values. */
export interface RateCardCategoryInput {
  id: string;
  name: string;
  active: boolean;
  /** Avg Project Mix %, `numeric(5,2)`. */
  projectMixPercent: string;
  /** Avg Internal Cost $/hr, `numeric(10,2)`. Single-valued — never per Rate Basis. */
  avgInternalCost: string;
}

/** One effective bill rate for a (Labor Category × Rate Basis). */
export interface RateCardEntryInput {
  laborCategoryId: string;
  rateBasisId: string;
  /** `numeric(10,2)`. */
  billRate: string;
}

export interface RateBasisInput {
  id: string;
  code: string;
  name: string;
  active: boolean;
}

// ── Outputs ──────────────────────────────────────────────────────────────────

export interface BasisMetrics {
  basisId: string;
  code: string;
  name: string;
  /**
   * False when any active Labor Category has no bill rate on this basis. An incomplete
   * basis is visible in Configuration and flagged, but can never be resolved onto a plan
   * (RP-12.1) — blendedRateMicros and cardMarginBp are `null`, never 0 and never borrowed
   * from another column. A silent fallback is how a Support engagement gets quoted at
   * Strategic rates without anyone noticing.
   */
  complete: boolean;
  /** Names of the active categories missing a rate — surfaced verbatim to [FIN]. */
  missingCategoryNames: string[];
  blendedRateMicros: Micros | null;
  cardMarginBp: Bp | null;
}

export interface CardMetrics {
  /** Σ of active categories' mix, in bp. Must equal FULL_PERCENT_BP to save (RP-12.1). */
  mixTotalBp: Bp;
  mixBalanced: boolean;
  /**
   * Σ (mix % × Avg Internal Cost), exact and unrounded. $82.2645 on the 2026 card.
   * Commercially sensitive — stripped at the API boundary for anyone lacking
   * `viewPlanCost` (non-negotiable 3).
   */
  blendedCostMicros: Micros;
  perBasis: BasisMetrics[];
}

// ── The card ─────────────────────────────────────────────────────────────────

/**
 * Compute every derived figure on a Rate Card.
 *
 * Only ACTIVE Labor Categories participate: the mix must sum to 100% across active rows,
 * and a deactivated category is not part of the firm's standard distribution. Only ACTIVE
 * Rate Bases are reported.
 */
export function computeCardMetrics(input: {
  categories: readonly RateCardCategoryInput[];
  entries: readonly RateCardEntryInput[];
  bases: readonly RateBasisInput[];
}): CardMetrics {
  const activeCategories = input.categories.filter((c) => c.active);

  let mixTotalBp = 0;
  let blendedCostMicros = 0;
  // Parse once — every basis reuses the same mix and the same cost.
  const parsed = activeCategories.map((category) => {
    const mixBp = parseMinor(category.projectMixPercent, 2);
    const costCents = parseMinor(category.avgInternalCost, 2);
    mixTotalBp += mixBp;
    blendedCostMicros += costCents * mixBp;
    return { id: category.id, name: category.name, mixBp };
  });
  assertSafe(blendedCostMicros, "blendedCostMicros");

  // (laborCategoryId, rateBasisId) → bill rate in cents.
  const rateByPair = new Map<string, Cents>();
  for (const entry of input.entries) {
    rateByPair.set(`${entry.laborCategoryId} ${entry.rateBasisId}`, parseMinor(entry.billRate, 2));
  }

  const perBasis = input.bases
    .filter((basis) => basis.active)
    .map((basis): BasisMetrics => {
      const missingCategoryNames: string[] = [];
      let blendedRateMicros = 0;

      for (const category of parsed) {
        const rateCents = rateByPair.get(`${category.id} ${basis.id}`);
        if (rateCents === undefined) {
          missingCategoryNames.push(category.name);
          continue;
        }
        blendedRateMicros += rateCents * category.mixBp;
      }

      const complete = missingCategoryNames.length === 0 && parsed.length > 0;
      if (!complete) {
        return {
          basisId: basis.id,
          code: basis.code,
          name: basis.name,
          complete: false,
          missingCategoryNames,
          blendedRateMicros: null,
          cardMarginBp: null,
        };
      }

      assertSafe(blendedRateMicros, `blendedRateMicros(${basis.code})`);
      return {
        basisId: basis.id,
        code: basis.code,
        name: basis.name,
        complete: true,
        missingCategoryNames,
        blendedRateMicros,
        // Computed from the UNROUNDED blended cost. See the header — pre-rounding turns
        // Fixed's 64.84% into 64.85% and fails the M1 acceptance test.
        cardMarginBp: marginBp(blendedRateMicros, blendedCostMicros),
      };
    });

  return {
    mixTotalBp,
    mixBalanced: mixTotalBp === FULL_PERCENT_BP,
    blendedCostMicros,
    perBasis,
  };
}

/**
 * (revenue − cost) ÷ revenue, as bp, rounded half-up exactly once.
 *
 * Returns null at zero revenue: a margin on nothing is undefined, not 0% and not 100%.
 * Both arguments must be in the same unit.
 */
export function marginBp(revenue: number, cost: number): Bp | null {
  if (revenue === 0) return null;
  return divideRoundHalfUp((revenue - cost) * FULL_PERCENT_BP, revenue);
}

/** Exact integer division with half-up rounding, symmetric about zero. */
function divideRoundHalfUp(numerator: number, denominator: number): number {
  assertSafe(numerator, "margin numerator");
  const sign = numerator < 0 !== denominator < 0 ? -1 : 1;
  const n = Math.abs(numerator);
  const d = Math.abs(denominator);
  return sign * Math.floor((2 * n + d) / (2 * d));
}

// ── Margin floor policy (RP-D3 / RP-14.1) ────────────────────────────────────

export type MarginStatus = "green" | "amber" | "red";

export interface MarginPolicy {
  floorBp: Bp;
  amberBandBp: Bp;
  /** Whether this basis inherits the firm-wide figure or overrides it — shown on the card. */
  floorSource: "global" | "basis";
  amberBandSource: "global" | "basis";
}

/**
 * Resolve the effective margin floor for a Rate Basis. A NULL override inherits the
 * firm-wide `system_config` value; the Rate Card shows which bases inherit and which
 * override (RP-14.1).
 *
 * Floors are ABSOLUTE percentages, never offsets from Card Margin: "is this deal below
 * 50%?" must stay answerable without knowing what the card blended to that quarter, and a
 * relative floor would move silently every time rates changed.
 */
export function resolveMarginPolicy(
  basis: { marginFloorPercent: string | null; marginAmberBandPoints: string | null },
  globals: { floorBp: Bp; amberBandBp: Bp },
): MarginPolicy {
  const override = basis.marginFloorPercent;
  const bandOverride = basis.marginAmberBandPoints;
  return {
    floorBp: override === null ? globals.floorBp : parseMinor(override, 2),
    floorSource: override === null ? "global" : "basis",
    amberBandBp: bandOverride === null ? globals.amberBandBp : parseMinor(bandOverride, 2),
    amberBandSource: bandOverride === null ? "global" : "basis",
  };
}

/**
 * Floor badge (RP-7.1): green at or above floor + band · amber at or above floor but
 * within the band · red below floor.
 *
 * Amber is a WARNING ABOVE THE FLOOR, not a shade of failure below it. Red maps 1:1 to
 * "this needs approval", so the badge and the approval gate can never say different
 * things.
 */
export function marginStatus(marginValueBp: Bp, policy: MarginPolicy): MarginStatus {
  if (marginValueBp < policy.floorBp) return "red";
  if (marginValueBp < policy.floorBp + policy.amberBandBp) return "amber";
  return "green";
}

// ── Presentation boundary — the ONLY place rounding happens ──────────────────

/** Micros → a 2dp dollar string, half-up. `82_264_500` → `"82.26"`. */
export function formatMicrosAsMoney(micros: Micros): string {
  return formatScaled(divideRoundHalfUp(micros, MICROS_PER_DOLLAR / 100), 2);
}

/** Bp → a 2dp percent string. `6484` → `"64.84"`. */
export function formatBpAsPercent(value: Bp): string {
  return formatScaled(value, 2);
}

function formatScaled(minor: number, scale: number): string {
  const sign = minor < 0 ? "-" : "";
  const digits = Math.abs(minor)
    .toString()
    .padStart(scale + 1, "0");
  const cut = digits.length - scale;
  return `${sign}${digits.slice(0, cut)}.${digits.slice(cut)}`;
}
